import { useTranslation } from "react-i18next";
import { LogIn, LogOut, BedDouble, Wallet } from "lucide-react";
import { formatCurrency } from "@/lib/format";

/**
 * Four large operational KPI cards.
 * Layout inspired by the reference dashboard — icon tile top-left,
 * label small above value, big value with optional secondary chip.
 * Uses subtle tinted icon backgrounds. No colored side borders.
 */
export default function KpiCards({ overview, currency, canSeeRevenue }) {
    const { t } = useTranslation();

    const cards = [
        {
            key: "arrivals",
            icon: LogIn,
            iconWrap: "bg-emerald-50 text-emerald-600",
            label: t("dashboard.arrivals_today"),
            value: overview?.arrivals_today ?? 0,
        },
        {
            key: "departures",
            icon: LogOut,
            iconWrap: "bg-rose-50 text-rose-600",
            label: t("dashboard.departures_today"),
            value: overview?.departures_today ?? 0,
        },
        {
            key: "occupancy",
            icon: BedDouble,
            iconWrap: "bg-violet-50 text-violet-600",
            label: t("dashboard.occupancy"),
            value: overview ? `${overview.occupied ?? 0} / ${overview.total_rooms ?? 0}` : "—",
            chip: overview ? `${overview.occupancy_pct ?? 0}%` : null,
        },
        {
            key: "revenue",
            icon: Wallet,
            iconWrap: "bg-amber-50 text-amber-600",
            label: t("dashboard.revenue_today"),
            value: formatCurrency(overview?.revenue_today || 0, currency),
            hidden: !canSeeRevenue,
        },
    ].filter((c) => !c.hidden);

    return (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="kpi-cards">
            {cards.map((c) => {
                const Icon = c.icon;
                const isLong = typeof c.value === "string" && c.value.length > 10;
                return (
                    <div
                        key={c.key}
                        data-testid={`kpi-${c.key}`}
                        className="flex items-center gap-4 rounded-[10px] border border-border bg-card p-4 shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
                    >
                        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[9px] ${c.iconWrap}`}>
                            <Icon size={20} strokeWidth={2} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-[12px] text-muted-foreground">
                                {c.label}
                            </div>
                            <div className="mt-0.5 flex items-baseline gap-2">
                                <div
                                    className={`font-semibold tracking-tight text-foreground tabular-nums ${
                                        isLong ? "text-[22px]" : "text-[26px]"
                                    } leading-none`}
                                >
                                    {c.value}
                                </div>
                                {c.chip && (
                                    <span className="rounded bg-[#ff7b02]/10 px-1.5 py-0.5 text-[11px] font-semibold text-[#ff7b02]">
                                        {c.chip}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
